import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { FormValidation } from '../../../shared/forms/form-validation';
import { FormUtility } from '../../../shared/forms/form-utility';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { FileUploadComponent } from '../../../shared/file-upload/file-upload.component';
import { FileTypeConstants } from '../../../shared/constants/file-upload-type-constants';

@Component({
  selector: 'claim-customer',
  templateUrl: './claim-customer.component.html',
})

/*
 * This has become the first step of the Report a Claim form submission.
 * The name could be updated to something more generic.
 */
export class ClaimCustomerComponent implements OnInit {

  @ViewChild(FileUploadComponent) fileUpload: FileUploadComponent;

  public claimCustomerForm: UntypedFormGroup;
  public formCompleted = false;
  
  public mbiWarningDialog: MatDialogRef<DialogComponent>;
  private readonly mbiPrefixes = ["MBI", "MBIQ", "MBC"];

  public readonly minCurrentMileage = 0;
  public readonly maxCurrentMileage = 9999999;

  public acceptedFiles: string[] = [FileTypeConstants.txt,
                                    FileTypeConstants.doc,
                                    FileTypeConstants.docx,
                                    FileTypeConstants.pdf,
                                    FileTypeConstants.jpeg,
                                    FileTypeConstants.jpg,
                                    FileTypeConstants.png,
                                    FileTypeConstants.PNG,
                                    FileTypeConstants.gif];

  constructor(private dialog: MatDialog) { }

  ngOnInit(): void {
    this.claimCustomerForm = new UntypedFormGroup({
      'customerFirstName': new UntypedFormControl("", [Validators.required]),
      'customerLastName': new UntypedFormControl("", [Validators.required]),
      'contractNumber': new UntypedFormControl("", [Validators.required]),
      'vin': new UntypedFormControl("", [Validators.required, Validators.pattern(/^[A-Za-z0-9]{6}$/)]),
      'breakdownDate': new UntypedFormControl("", [Validators.required]),
      'currentMileage': new FormControl<number>(null, [Validators.required]),
      'customerConcern': new UntypedFormControl("", [Validators.required]),
      'causeOfFailure': new UntypedFormControl("", [Validators.required])
    });
  }

  /*
   * Enforces contractNumber validation rules when field focus is lost.
   */
  public onContractNumberBlur(): void {
    let controlValue = this.claimCustomerForm.controls['contractNumber']?.value;
    if (this.mbiPrefixes.some(p => String(controlValue)?.trim().startsWith(p))) {
      this.showMbiWarningDialog();
    }
  }

  /*
   * Enforces max length of currentMileage field.
   */
  public onCurrentMileageChange(): void {
    let controlValue = this.claimCustomerForm.controls['currentMileage']?.value;
    if (controlValue < this.minCurrentMileage) {
      this.claimCustomerForm.controls['currentMileage'].setValue(this.minCurrentMileage);
    }
    else if (controlValue > this.maxCurrentMileage) {
      // If entering a a digit past max, truncate it.
      var targetValue = String(controlValue).substring(0, 6);
      this.claimCustomerForm.controls['currentMileage'].setValue(targetValue);
    }
  }
  
  //** Validates the customer form and sets formCompleted to valid or invalid */
  public validateForm(): void {
    this.formCompleted = FormValidation.isValid(this.claimCustomerForm);
  }

  /**
   * Resets the form.
   * @see {@link FormUtility.reset}
   */
  public reset(): void {
    FormUtility.reset(this.claimCustomerForm);
    this.fileUpload.clearFiles();
    this.formCompleted = false;
  }

  /**
   * Clears the value in the specified form field.
   * @param field The form field to clear.
   */
  public clear(field: string): void {
    FormUtility.clear(this.claimCustomerForm, field);
  }

  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.claimCustomerForm, field)
  }

  public showMbiWarningDialog() {
    this.mbiWarningDialog = this.dialog.open(DialogComponent, {
      data: {
        title: "Claim cannot be submitted online",
        bodyText: "We appreciate your business, unfortunately your claim cannot be submitted online. To proceed with your claim, please contact our support team at 1-800-531-1925 for further assistance. Our representatives are standing by to help you complete your submission.",
        confirmationButtonText: "Close"
      }
    });

    this.mbiWarningDialog.afterClosed().subscribe(_ => {
      // Result does not matter. Always clear the contract number.
      this.claimCustomerForm.controls['contractNumber'].setValue('');
    });
  }
}
